import { METHOD_IDS } from '@/components/admin/reglages/method-contracts';
import { MethodField } from '@/components/admin/reglages/method-field';
import {
  METHOD_FUNCTION_FIELDS,
  type MethodDraft,
  type MethodFunctionField,
  differsFrom,
} from '@/lib/method-draft';
import { cn } from '@/lib/utils';
import {
  ANALYST_FUNCTION_NAMES,
  type AnalystFunction,
  type MethodContent,
} from '@matter_extractor/shared';
import { useId } from 'react';

/**
 * One FUNCTION of Réglages > Méthode: Souverain, Guerrier or Producteur, with what
 * it designates and the words of the spoken language that carry it (analyste.md
 * §4.2, maquette `Methode`) - the two fields the first pass reads.
 *
 * The letter in its chip is the one the method gives it in a triplet (S, G, P).
 * Its ground is a token of the charte and not a hex of the planche: the three
 * the planche paints are the darkened inks of the noyaux, and `bleu-nuit`,
 * `rouge-deep` and `vert-deep` are the tokens that carry white text at the
 * contrast the planche was after.
 */

export interface MethodFunctionCardProps {
  readonly fn: AnalystFunction;
  readonly draft: MethodDraft;
  /** The development version the draft started from: what « modifié » is measured against. */
  readonly stored: MethodContent;
  /** A version opened by « Voir », or a vitrine: nothing is typable, and nothing is « modifié ». */
  readonly readOnly: boolean;
  readonly onField: (field: MethodFunctionField, text: string) => void;
}

/** The ground of the chip of each function - the Souverain is the blue of the charte. */
const CHIP: Readonly<Record<AnalystFunction, string>> = {
  S: 'bg-bleu-nuit',
  G: 'bg-rouge-deep',
  P: 'bg-vert-deep',
};

export function MethodFunctionCard({
  fn,
  draft,
  stored,
  readOnly,
  onField,
}: MethodFunctionCardProps) {
  const titleId = useId();
  return (
    <section
      aria-labelledby={titleId}
      data-testid={METHOD_IDS.functionCard}
      data-function={fn}
      className="flex min-w-0 flex-col gap-3 rounded-lg border-2 border-gray-200 bg-white p-4"
    >
      <div className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className={cn(
            'inline-flex size-5.5 shrink-0 items-center justify-center rounded-sm font-bold text-white text-xs leading-none',
            CHIP[fn],
          )}
        >
          {fn}
        </span>
        <h3 id={titleId} className="font-semibold text-gray-800 text-sm leading-4.5">
          {ANALYST_FUNCTION_NAMES[fn]}
        </h3>
      </div>

      {METHOD_FUNCTION_FIELDS.map((field) => {
        const ref = { part: 'functions', key: fn, field } as const;
        return (
          <MethodField
            key={field}
            fieldRef={ref}
            value={draft.functions[fn][field]}
            touched={!readOnly && differsFrom(draft, stored, ref)}
            readOnly={readOnly}
            onChange={(text) => onField(field, text)}
          />
        );
      })}
    </section>
  );
}
